import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { queryKeys } from '@constants/queryKeys.constant';
import { EProviderRole } from '@enums/provider.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { cn } from '@lib/utils';
import { paginationSchema } from '@schemas/pagination.schema';
import { providerService } from '@services/providers/service';

import { ProviderActivityTabs } from './@components/ActivityTabs';
import { ProviderCompany } from './@components/Company';
import { ProviderData } from './@components/Data';
import { ProviderDetailsLoading } from './@components/Loading';
import { ProviderTabs } from './@components/Tabs';

function providerQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.providers.detail(id),
    queryFn: () => providerService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute('/_layout/(private)/providers/$id/')({
  component: ProviderDetails,
  validateSearch: search => paginationSchema.parse(search),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(providerQueryOptions(params.id)),
  pendingComponent: ProviderDetailsLoading,
  pendingMs: 300,
  pendingMinMs: 600,
});

function ProviderDetails() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(providerQueryOptions(id));

  return (
    <div className="grid py-4 lg:p-0">
      <div
        className={cn(
          'border-secondary mb-6 grid gap-y-8 border-b-2 border-dotted pb-2 lg:grid-cols-2 lg:gap-4',
          {
            'xl:grid-cols-3': data.provider.role === EProviderRole.External,
          },
        )}
      >
        <ProviderData data={data.provider} />

        {data.provider.role === EProviderRole.External && <ProviderCompany />}

        <ProviderTabs counts={data.provider.counts} id={data.provider.id} />
      </div>

      <ProviderActivityTabs />
    </div>
  );
}
